import React from 'react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import {
  reassignElementsToLayer,
  updateElement,
  updateElements,
  patchProjectMetadata,
  getElementPorts,
} from '../../features/layout/layoutSlice';
import { resolvePorts } from '../../utils/equipmentGeometry';
import { LayoutElement } from '../../types';
import { COMPONENT_SCHEMAS, CLASS_LABELS } from '../../constants';
import { DEFAULT_STOCKER_PROCESS_WAIT_SEC } from '../../features/equipment/equipmentConstants';
import {
  ALL_RAIL_TYPES,
  resolveRailType,
  isJunctionRailType,
  findNearestRailForOHT,
} from '../../utils/topology';
import {
  canReverseTrackDirection,
  reverseUnidirectionalTrack,
} from '../../utils/trackDirectionMarker';
import {
  metersToEffectiveWorldUnits,
  shouldUseDxfCalibration,
  worldUnitsToEffectiveMeters,
} from '../../utils/measurement';
import {
  defaultElementZ,
  getElement3DExtrusionDepth,
  getElementElevationZ,
  elementUses3DElevation,
  heightMToCanvasElevation,
  DEFAULT_MM_PER_CANVAS_UNIT,
  EQUIPMENT_3D_DEPTH,
  STOCKER_3D_DEPTH,
  TRACK_3D_ELEVATION,
  isFixedEquipment3DDepth,
  isDxfEquipmentLayoutElement,
} from '../../utils/layout3d';
import {
  primaryProcessType,
  readStockerProcessTypes,
} from '../../features/equipment/stockerProcessTypes';
import {
  resolveSharedOhtRouteTypes,
  OHT_ROLE_OPTIONS,
  parseOhtRole,
} from '../../features/simulation/ohtDedicatedEndpoints';
import { AMHS_PHYSICS_DEFAULTS } from '../../features/simulation/amhsSimulationConfig';
import { getJunctionWaitSec } from '../../utils/junctionTurntableSim';
import { resolveJunctionWaitSec } from '../../features/simulation/buildAmhsStreamRequest';
import PropertySection from './PropertySection';
import ProcessTypeSelect from './ProcessTypeSelect';
import LoadPortsEditor from './LoadPortsEditor';
import EquipmentInstanceProperties from './EquipmentInstanceProperties';
import LoadPortProperties from './LoadPortProperties';
import TrackNodeProperties from './TrackNodeProperties';
import { useSimulationTrackNodes } from '../../hooks/useSimulationTrackNodes';
import {
  PROPERTY_CONTROL_CLASS,
  PropertyCheckbox,
  PropertyColor,
  PropertyInput,
  PropertyNumber,
  PropertyPanelHeader,
  PropertyRow,
  PropertySelect,
  PropertyTooltip,
  PropertyToggle,
  ReadOnlyRow,
} from './PropertyFields';
import { useProjectProcessTypes } from '../../hooks/useProjectProcessTypes';

const OHT_MODE_TOOLTIP =
  'Dedicated follows its configured role and fixed path. Shared has no role or path filter and is available to the dispatcher as fallback capacity.';
const OHT_ROLE_TOOLTIP =
  '• Entry — Gate → stocker\n• Process bay — Stocker → matching tools → stocker\n• Outer loop — Entry → selected stockers → Exit\n• Inter bay — Stocker → stocker\n• Exit — Stocker → gate';

const PropertiesPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const selectedIds = useAppSelector((state) => state.selection.selectedIds);
  const elements = useAppSelector((state) => state.layout.elements);
  const portEditElementId = useAppSelector((state) => state.layout.portEditElementId);
  const layoutSelectedPortId = useAppSelector((state) => state.layout.selectedPortId);
  const layers = useAppSelector((state) => state.layout.layers);
  const layerOrder = useAppSelector((state) => state.layout.layerOrder);
  const floorPlanScaleFactor = useAppSelector((state) => state.floorplan.scaleFactor);
  const dxfUnitsPerMeter = useAppSelector((state) => state.floorplan.dxfUnitsPerMeter);
  const floorplanBounds = useAppSelector((state) => state.floorplan.bounds);
  const floorplanPolylineCount = useAppSelector((state) => state.floorplan.polylines.length);
  const equipmentInstances = useAppSelector((state) => state.equipment.instances);
  const equipmentTemplates = useAppSelector((state) => state.equipment.templates);
  const selectedEquipmentId = useAppSelector((state) => state.equipment.selectedInstanceId);
  const equipmentSelectedPortId = useAppSelector((state) => state.equipment.selectedPortId);
  const selectedTrackNodeId = useAppSelector((state) => state.layout.selectedTrackNodeId);
  const projectMetadata = useAppSelector((state) => state.layout.projectMetadata);
  const observedProcessTypes = [
    ...Object.values(equipmentInstances).flatMap((instance) => [
      instance.equipmentType,
      ...(instance.processTypes ?? []),
    ]),
    ...Object.values(elements).flatMap((element) => [
      element.customProperties?.processType,
      element.customProperties?.toolType,
      element.customProperties?.toToolType,
    ]),
  ];
  const { processTypes: processTypeOptions } = useProjectProcessTypes(observedProcessTypes);
  const trackNodes = useSimulationTrackNodes();
  const hasFloorPlanCalibration = shouldUseDxfCalibration(
    Boolean(floorplanBounds),
    floorplanPolylineCount
  );
  const effectiveScaleFactor = hasFloorPlanCalibration ? floorPlanScaleFactor : undefined;
  const effectiveDxfUnitsPerMeter = hasFloorPlanCalibration ? dxfUnitsPerMeter : undefined;

  const selectedElements = selectedIds
    .map((id) => elements[id])
    .filter((el): el is LayoutElement => !!el);
  const selectedElement = selectedIds.length === 1 ? selectedElements[0] : null;
  const orderedLayers = layerOrder.map((id) => layers[id]).filter(Boolean);
  const selectedEquipment = selectedEquipmentId ? equipmentInstances[selectedEquipmentId] : null;

  // Stockers and DXF-imported tools both carry editable load ports, unless an
  // equipment overlay instance owns them instead (it renders its own editor).
  const layoutElementOwnsPorts = (element: LayoutElement | null): boolean => {
    if (!element) return false;
    const linkedOverlayId =
      typeof element.customProperties?.dxfEquipmentId === 'string'
        ? element.customProperties.dxfEquipmentId
        : null;
    if (linkedOverlayId && equipmentInstances[linkedOverlayId]) return false;
    return element.type === 'stocker' || isDxfEquipmentLayoutElement(element);
  };

  // ── Simulation track node ───────────────────────────────────────────────
  if (selectedTrackNodeId) {
    const trackNode = trackNodes.find((node) => node.nodeId === selectedTrackNodeId);
    if (trackNode) {
      return <TrackNodeProperties node={trackNode} />;
    }
  }

  // ── Load Port (equipment overlay) ───────────────────────────────────────
  if (selectedEquipment && equipmentSelectedPortId) {
    const template = selectedEquipment.templateId
      ? equipmentTemplates[selectedEquipment.templateId]
      : null;
    const port = resolvePorts(selectedEquipment, template).find(
      (p) => p.id === equipmentSelectedPortId
    );
    if (port) {
      return <LoadPortProperties kind="equipment" instance={selectedEquipment} port={port} />;
    }
  }

  // ── Load Port (layout stocker / tool) ───────────────────────────────────
  if (layoutElementOwnsPorts(selectedElement) && selectedElement && layoutSelectedPortId) {
    const port = getElementPorts(selectedElement).find((p) => p.id === layoutSelectedPortId);
    if (port) {
      return <LoadPortProperties kind="layout" element={selectedElement} port={port} />;
    }
  }

  // ── Equipment overlay instance (no layout selection) ────────────────────
  if (selectedEquipment && selectedElements.length === 0) {
    return <EquipmentInstanceProperties instance={selectedEquipment} />;
  }

  const currentLayerIdForSelection = (() => {
    if (selectedElements.length === 0) return '';
    const first = selectedElements[0].layerId;
    return selectedElements.every((el) => el.layerId === first) ? first : '__mixed__';
  })();

  const handleLayerChange = (layerId: string) => {
    if (!layerId || layerId === '__mixed__' || selectedElements.length === 0) return;
    dispatch(
      reassignElementsToLayer({
        elementIds: selectedElements.map((el) => el.id),
        layerId,
      })
    );
  };

  if (selectedElements.length === 0) {
    return (
      <div className="p-2.5">
        <div className="py-5 text-center text-xs text-slate-400 dark:text-gray-500">
          No element selected
        </div>
      </div>
    );
  }

  if (!selectedElement) {
    return (
      <div className="p-2.5">
        <PropertyPanelHeader
          title={`${selectedElements.length} items selected`}
          subtitle="Multiple selection"
        />
        <PropertySection title="General">
          <PropertySelect
            label="Layer"
            value={currentLayerIdForSelection}
            onChange={handleLayerChange}
            options={[
              ...(currentLayerIdForSelection === '__mixed__'
                ? [{ value: '__mixed__', label: 'Mixed layers' }]
                : []),
              ...orderedLayers.map((layer) => ({ value: layer.id, label: layer.name })),
            ]}
          />
        </PropertySection>
      </div>
    );
  }

  const isCircleJunction =
    selectedElement.type === 'cross-rail' && resolveRailType(selectedElement) === 'junction-circle';
  const handlePositionChange = (axis: 'x' | 'y' | 'z', value: number) => {
    dispatch(
      updateElement({
        id: selectedElement.id,
        updates: {
          position: {
            ...selectedElement.position,
            [axis]: value,
          },
        },
      })
    );
  };

  const handleMetadataChange = (key: keyof LayoutElement['metadata'], value: unknown) => {
    dispatch(
      updateElement({
        id: selectedElement.id,
        updates: {
          metadata: {
            ...selectedElement.metadata,
            [key]: value,
          },
        },
      })
    );
  };

  const handleCustomPropChange = (key: string, value: unknown) => {
    if (key === 'junctionWaitSec') {
      const waitSec = Math.max(0, Number(value) || 0);
      dispatch(patchProjectMetadata({ junctionWaitSec: waitSec }));
      const junctionPatches = Object.values(elements)
        .filter(
          (el) => el.type === 'cross-rail' || isJunctionRailType(resolveRailType(el))
        )
        .map((el) => ({
          id: el.id,
          updates: {
            customProperties: {
              junctionWaitSec: waitSec,
              waitSec,
            },
          },
        }));
      if (junctionPatches.length > 0) {
        dispatch(updateElements(junctionPatches));
      }
      return;
    }

    dispatch(
      updateElement({
        id: selectedElement.id,
        updates: {
          customProperties: {
            ...(selectedElement.customProperties || {}),
            [key]: value,
          },
        },
      })
    );
  };

  const handleCustomPropsChange = (changes: Record<string, unknown>) => {
    dispatch(
      updateElement({
        id: selectedElement.id,
        updates: {
          customProperties: {
            ...(selectedElement.customProperties || {}),
            ...changes,
          },
        },
      })
    );
  };

  const handleMetersDimensionChange = (
    valueMeters: number,
    extraCustomProps?: Record<string, unknown>
  ) => {
    const safeMeters = Math.max(0, valueMeters);
    const mappedUnits = Math.max(
      1,
      metersToEffectiveWorldUnits(safeMeters, effectiveScaleFactor, effectiveDxfUnitsPerMeter)
    );
    const nextDimensions = {
      width: mappedUnits,
      height: selectedElement.dimensions.height,
    };

    dispatch(
      updateElement({
        id: selectedElement.id,
        updates: {
          dimensions: nextDimensions,
          customProperties: {
            ...(selectedElement.customProperties || {}),
            ...(extraCustomProps || {}),
            lengthMeters: safeMeters,
          },
        },
      })
    );
  };

  const schema = COMPONENT_SCHEMAS[selectedElement.type] || [];
  const sku = selectedElement.customProperties?.sku as string | undefined;
  const rawLength = selectedElement.dimensions.width;
  const calibratedLengthMeters = worldUnitsToEffectiveMeters(
    rawLength,
    effectiveScaleFactor,
    effectiveDxfUnitsPerMeter
  );
  const storedLengthMeters = Number(selectedElement.customProperties?.lengthMeters);
  const storedHeightMeters = Number(selectedElement.customProperties?.heightMeters);
  const lengthMeters = Number.isFinite(storedLengthMeters)
    ? storedLengthMeters
    : ALL_RAIL_TYPES.has(selectedElement.type)
      ? 3
      : calibratedLengthMeters;

  const mmPerCanvasUnit =
    effectiveScaleFactor && effectiveScaleFactor > 0
      ? effectiveScaleFactor
      : DEFAULT_MM_PER_CANVAS_UNIT;
  const isElevated = elementUses3DElevation(selectedElement.type);
  const isFixedEquipmentDepth =
    isFixedEquipment3DDepth(selectedElement.type) || isDxfEquipmentLayoutElement(selectedElement);
  const fixedDepthValue =
    selectedElement.type === 'stocker' ? STOCKER_3D_DEPTH : EQUIPMENT_3D_DEPTH;
  const elevationZ = isFixedEquipmentDepth
    ? fixedDepthValue
    : selectedElement.position.z !== undefined && selectedElement.position.z !== null
      ? selectedElement.position.z
      : defaultElementZ(selectedElement.type, selectedElement.customProperties);
  const effective3DValue = isElevated
    ? getElementElevationZ(selectedElement)
    : getElement3DExtrusionDepth(selectedElement);

  const isStocker = selectedElement.type === 'stocker';
  const isOht = selectedElement.type === 'oht-vehicle';
  const nearestRail = isOht
    ? findNearestRailForOHT(selectedElement, Object.values(elements))
    : null;
  const stockerProcessTypes = readStockerProcessTypes({
    processTypes: selectedElement.customProperties?.processTypes,
    legacyProcessType:
      selectedElement.customProperties?.processType ?? selectedElement.customProperties?.toolType,
  });
  const cruiseRaw = selectedElement.customProperties?.cruiseSpeedOverride;
  const cruiseSpeedOverride =
    typeof cruiseRaw === 'number' && Number.isFinite(cruiseRaw) ? cruiseRaw : null;

  // For Digital Twin stocker/OHT, drive fields from dedicated sections (not the
  // generic Parameters list). Rails and other types still use COMPONENT_SCHEMAS.
  const schemaFields = isStocker || isOht ? [] : schema;

  return (
    <div className="p-2.5">
      <PropertyPanelHeader
        title={selectedElement.metadata.name || CLASS_LABELS[selectedElement.type]}
        subtitle={CLASS_LABELS[selectedElement.type]}
        color={selectedElement.metadata.color || '#cccccc'}
        actions={
          canReverseTrackDirection(selectedElement) ? (
            <button
              type="button"
              className="shrink-0 rounded-md border border-slate-200 bg-white px-1.5 py-1 text-[10px] font-medium text-slate-600 transition-colors hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              onClick={() => {
                const updates = reverseUnidirectionalTrack(selectedElement);
                if (!updates) return;
                dispatch(
                  updateElement({
                    id: selectedElement.id,
                    updates,
                  })
                );
              }}
            >
              Switch direction
            </button>
          ) : null
        }
      />

      <div className="space-y-1.5">
        <PropertySection title="General">
          <PropertyInput
            label="Name"
            value={selectedElement.metadata.name}
            onChange={(value) => handleMetadataChange('name', value)}
          />
          {isStocker && (
            <>
              <PropertyInput
                label="Description"
                value={String(selectedElement.customProperties?.description ?? '')}
                onChange={(value) => handleCustomPropChange('description', value)}
              />
              <PropertyRow label="Process Type" wide>
                <ProcessTypeSelect
                  value={primaryProcessType(stockerProcessTypes) ?? ''}
                  options={processTypeOptions}
                  allowNone
                  className={PROPERTY_CONTROL_CLASS}
                  onChange={(value) => {
                    const processType = value || null;
                    handleCustomPropsChange({
                      processTypes: processType ? [processType] : [],
                      processType,
                    });
                  }}
                />
              </PropertyRow>
              <PropertyNumber
                label="Wait Time"
                unit="s"
                value={
                  typeof selectedElement.customProperties?.processWaitSec === 'number' &&
                  Number.isFinite(selectedElement.customProperties.processWaitSec)
                    ? selectedElement.customProperties.processWaitSec
                    : DEFAULT_STOCKER_PROCESS_WAIT_SEC
                }
                min={0}
                step={1}
                onChange={(value) =>
                  handleCustomPropChange('processWaitSec', Math.max(0, value))
                }
              />
            </>
          )}
          {!isStocker && !isOht && (
            <>
              <PropertyColor
                label="Color"
                value={selectedElement.metadata.color || '#cccccc'}
                onChange={(value) => handleMetadataChange('color', value)}
              />
              <ReadOnlyRow label="Class" value={CLASS_LABELS[selectedElement.type]} />
              {sku && <ReadOnlyRow label="SKU" value={sku} />}
            </>
          )}
          {(isStocker || isOht) && (
            <ReadOnlyRow label="Class" value={CLASS_LABELS[selectedElement.type]} />
          )}
          <PropertySelect
            label="Layer"
            value={selectedElement.layerId}
            onChange={handleLayerChange}
            options={orderedLayers.map((layer) => ({ value: layer.id, label: layer.name }))}
          />
        </PropertySection>

        {isOht && (
          <PropertySection title="Placement">
            {nearestRail ? (
              <ReadOnlyRow label="Current Rail" value={nearestRail.rail.metadata.name} />
            ) : (
              <ReadOnlyRow label="Placement" value="Not on rail" />
            )}
          </PropertySection>
        )}

        {isOht && (
          <PropertySection title="Fleet role">
            <PropertyToggle
              label={
                <>
                  Mode
                  <PropertyTooltip text={OHT_MODE_TOOLTIP} />
                </>
              }
              value={String(selectedElement.customProperties?.ohtMode ?? 'dedicated')}
              options={[
                { value: 'dedicated', label: 'Dedicated' },
                { value: 'shared', label: 'Shared' },
              ]}
              onChange={(value) => {
                if (value === 'shared') {
                  const existing = Array.isArray(
                    selectedElement.customProperties?.allowedRouteTypes
                  )
                    ? (selectedElement.customProperties.allowedRouteTypes as string[])
                    : null;
                  dispatch(
                    updateElement({
                      id: selectedElement.id,
                      updates: {
                        customProperties: {
                          ...(selectedElement.customProperties || {}),
                          ohtMode: 'shared',
                          allowedRouteTypes: resolveSharedOhtRouteTypes(existing),
                        },
                      },
                    })
                  );
                  return;
                }
                dispatch(
                  updateElement({
                    id: selectedElement.id,
                    updates: {
                      customProperties: {
                        ...(selectedElement.customProperties || {}),
                        ohtMode: 'dedicated',
                        ohtRole: parseOhtRole(selectedElement.customProperties?.ohtRole) ?? 'group',
                        dedicatedFrom: null,
                        dedicatedTo: null,
                        allowedRouteTypes: null,
                      },
                    },
                  })
                );
              }}
            />
            {String(selectedElement.customProperties?.ohtMode ?? 'dedicated') !== 'shared' && (
              <>
                <PropertySelect
                  label={
                    <>
                      Role
                      <PropertyTooltip text={OHT_ROLE_TOOLTIP} />
                    </>
                  }
                  value={parseOhtRole(selectedElement.customProperties?.ohtRole) ?? 'group'}
                  options={[...OHT_ROLE_OPTIONS]}
                  onChange={(value) => {
                    const role = parseOhtRole(value) ?? 'group';
                    const existingToolType = String(
                      selectedElement.customProperties?.toolType ?? ''
                    )
                      .trim()
                      .toUpperCase();
                    const defaultToolType = existingToolType || processTypeOptions[0] || 'PHOTO';
                    const existingToToolType = String(
                      selectedElement.customProperties?.toToolType ?? ''
                    )
                      .trim()
                      .toUpperCase();
                    const defaultToToolType =
                      existingToToolType ||
                      processTypeOptions.find((t) => t !== defaultToolType) ||
                      processTypeOptions[1] ||
                      'ETCH';
                    dispatch(
                      updateElement({
                        id: selectedElement.id,
                        updates: {
                          customProperties: {
                            ...(selectedElement.customProperties || {}),
                            ohtRole: role,
                            dedicatedFrom: null,
                            dedicatedTo: null,
                            allowedRouteTypes: null,
                            ...(role === 'interbay'
                              ? {
                                  toolType: defaultToolType,
                                  toToolType: defaultToToolType,
                                }
                              : {
                                  toolType: defaultToolType,
                                  toToolType: null,
                                }),
                          },
                        },
                      })
                    );
                  }}
                />
                {(() => {
                  const role = parseOhtRole(selectedElement.customProperties?.ohtRole) ?? 'group';
                  const toolTypeOptions = processTypeOptions;
                  const toolType =
                    String(selectedElement.customProperties?.toolType ?? '').trim() ||
                    toolTypeOptions[0] ||
                    'PHOTO';
                  const toToolType =
                    String(selectedElement.customProperties?.toToolType ?? '').trim() ||
                    toolTypeOptions.find((t) => t !== toolType) ||
                    toolTypeOptions[0] ||
                    'ETCH';
                  const processTypeLabel = role === 'interbay' ? 'From process' : 'Process type';
                  const processTypeTooltip =
                    role === 'entry'
                      ? 'Selects the stocker this OHT serves after entering through the gate.'
                      : role === 'exit'
                        ? 'Selects the stocker this OHT serves before leaving through the exit gate.'
                        : role === 'interbay'
                          ? 'Selects the source stocker process; To process selects the destination.'
                          : 'Selects the matching stocker and tools served by this process-bay OHT.';
                  if (role === 'outerloop') {
                    return (
                      <>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400">
                          Visit order follows Configure process flow and is shared by every outer
                          OHT: Entry → process-sequence stockers → Exit.
                        </p>
                      </>
                    );
                  }
                  return (
                    <>
                      <PropertySelect
                        label={
                          <>
                            {processTypeLabel}
                            <PropertyTooltip text={processTypeTooltip} />
                          </>
                        }
                        value={toolType}
                        options={toolTypeOptions}
                        onChange={(value) => handleCustomPropChange('toolType', value)}
                      />
                      {role === 'interbay' && (
                        <PropertySelect
                          label={
                            <>
                              To process
                              <PropertyTooltip text="Selects the destination stocker process for this inter-bay OHT." />
                            </>
                          }
                          value={toToolType}
                          options={toolTypeOptions}
                          onChange={(value) => handleCustomPropChange('toToolType', value)}
                        />
                      )}
                    </>
                  );
                })()}
              </>
            )}
            {String(selectedElement.customProperties?.ohtMode ?? '') === 'shared' && (
              <p className="text-[10px] text-gray-500 dark:text-gray-400">
                Shared OHTs are used when no dedicated OHT matches. No corridor or role filters are
                sent for shared mode.
              </p>
            )}
          </PropertySection>
        )}

        {isOht && (
          <PropertySection title="Simulation">
            <PropertyNumber
              label="Cruise Speed"
              unit="m/s"
              value={cruiseSpeedOverride ?? AMHS_PHYSICS_DEFAULTS.ohtCruiseSpeed}
              min={0}
              step={0.1}
              hint={`Default ${AMHS_PHYSICS_DEFAULTS.ohtCruiseSpeed} m/s.`}
              onChange={(value) =>
                handleCustomPropChange(
                  'cruiseSpeedOverride',
                  value >= 0 ? value : AMHS_PHYSICS_DEFAULTS.ohtCruiseSpeed
                )
              }
            />
          </PropertySection>
        )}

        {/* Component Parameters (parametric properties) — rails & others */}
        {schemaFields.length > 0 && (
          <PropertySection title="Parameters">
            {schemaFields.map((field) => {
              const current =
                selectedElement.customProperties?.[field.key] !== undefined
                  ? selectedElement.customProperties[field.key]
                  : (selectedElement as LayoutElement & Record<string, unknown>)[field.key];
              if (field.type === 'select') {
                if (field.key === 'direction' && isCircleJunction) return null;

                const selectValue =
                  field.key === 'heightM'
                    ? String(
                        Number.isFinite(storedHeightMeters)
                          ? storedHeightMeters
                          : current != null
                            ? current
                            : ''
                      )
                    : current != null
                      ? String(current)
                      : '';
                if (field.key === 'direction') {
                  return (
                    <PropertyToggle
                      key={field.key}
                      label={field.label}
                      value={selectValue}
                      options={[
                        { value: 'uni', label: 'Uni' },
                        { value: 'bi', label: 'Bi' },
                      ]}
                      onChange={(value) => handleCustomPropChange(field.key, value)}
                    />
                  );
                }
                return (
                  <PropertySelect
                    key={field.key}
                    label={field.unit ? `${field.label} (${field.unit})` : field.label}
                    value={selectValue}
                    options={field.options || []}
                    onChange={(value) => {
                      if (field.key === 'heightM') {
                        const meters = Number(value);
                        handleCustomPropChange('heightM', value);
                        handleCustomPropChange(
                          'heightMeters',
                          Number.isFinite(meters) ? meters : 0
                        );
                        if (ALL_RAIL_TYPES.has(selectedElement.type)) {
                          dispatch(
                            updateElement({
                              id: selectedElement.id,
                              updates: {
                                position: {
                                  ...selectedElement.position,
                                  z: heightMToCanvasElevation(meters, mmPerCanvasUnit),
                                },
                              },
                            })
                          );
                        }
                        return;
                      }
                      if (field.key === 'bayCount') {
                        handleCustomPropChange('bayCount', value);
                        const count = String(value);
                        let widthM = 2.645;
                        if (count === '6') widthM = 3.36;
                        else if (count === '8') widthM = 4.075;

                        const mappedUnits = Math.max(
                          1,
                          metersToEffectiveWorldUnits(
                            widthM,
                            effectiveScaleFactor,
                            effectiveDxfUnitsPerMeter
                          )
                        );

                        dispatch(
                          updateElement({
                            id: selectedElement.id,
                            updates: {
                              dimensions: {
                                ...selectedElement.dimensions,
                                width: mappedUnits,
                              },
                            },
                          })
                        );
                        return;
                      }
                      handleCustomPropChange(field.key, value);
                    }}
                  />
                );
              }
              if (field.type === 'number') {
                const projectJunctionWait = resolveJunctionWaitSec(projectMetadata);
                const numberValue =
                  field.key === 'lengthMeters'
                    ? lengthMeters
                    : field.key === 'junctionWaitSec'
                      ? (projectJunctionWait ?? getJunctionWaitSec(selectedElement))
                    : typeof current === 'number'
                      ? current
                      : Number(current) || 0;

                // Hide lengthMeters for curves
                const railType = selectedElement.customProperties?.railType;
                if (
                  field.key === 'lengthMeters' &&
                  (railType === 'curve90' || railType === 'curve45' || railType === 'curve180')
                )
                  return null;
                return (
                  <div key={field.key}>
                    <PropertyNumber
                      label={field.unit ? `${field.label} (${field.unit})` : field.label}
                      value={numberValue}
                      min={field.key === 'junctionWaitSec' ? 0 : undefined}
                      step={field.key === 'junctionWaitSec' ? 0.1 : 0.01}
                      onChange={(value) => {
                        if (field.key === 'lengthMeters') {
                          handleMetersDimensionChange(value);
                          return;
                        }
                        handleCustomPropChange(field.key, value);
                      }}
                    />
                  </div>
                );
              }
              if (field.type === 'boolean') {
                const booleanValue = current !== false && current !== 'false';
                return (
                  <div key={field.key}>
                    <PropertyCheckbox
                      label={field.label}
                      checked={booleanValue}
                      onChange={(value) => handleCustomPropChange(field.key, value)}
                    />
                  </div>
                );
              }
              return (
                <PropertyInput
                  key={field.key}
                  label={field.label}
                  value={current != null ? String(current) : ''}
                  onChange={(value) => handleCustomPropChange(field.key, value)}
                />
              );
            })}
          </PropertySection>
        )}

        <PropertySection title="Geometry">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Position</div>
          <div className="grid grid-cols-2 gap-2">
            <PropertyNumber
              label="X"
              value={selectedElement.position.x}
              onChange={(value) => handlePositionChange('x', value)}
              step={0.01}
            />
            <PropertyNumber
              label="Y"
              value={selectedElement.position.y}
              onChange={(value) => handlePositionChange('y', value)}
              step={0.01}
            />
          </div>
          <div className="mt-2">
            <PropertyNumber
              label={isElevated ? 'Z (3D elevation)' : 'Z (3D depth)'}
              value={
                isElevated
                  ? TRACK_3D_ELEVATION
                  : selectedElement.type === 'stocker'
                    ? 30
                    : elevationZ
              }
              onChange={(value) => handlePositionChange('z', value)}
              step={1}
              min={0}
              disabled={isElevated || selectedElement.type === 'stocker'}
            />
            <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
              {isFixedEquipmentDepth
                ? `Fixed ${selectedElement.type === 'stocker' ? 'stocker' : 'equipment'} depth: ${fixedDepthValue} (mandatory)`
                : isElevated
                  ? `Fixed elevation: ${TRACK_3D_ELEVATION} (not editable)`
                  : `Canvas units · extrusion depth in 3D (effective: ${effective3DValue.toFixed(1)})`}
            </p>
          </div>
        </PropertySection>

        {layoutElementOwnsPorts(selectedElement) && (
          <PropertySection title="Ports">
            <LoadPortsEditor
              kind="layout"
              element={selectedElement}
              editing={portEditElementId === selectedElement.id}
            />
          </PropertySection>
        )}

        <PropertySection title="Visibility & Lock" defaultOpen={false}>
          <PropertyCheckbox
            label="Visible"
            checked={selectedElement.metadata.visible}
            onChange={(checked) => handleMetadataChange('visible', checked)}
          />
          <PropertyCheckbox
            label="Locked"
            checked={selectedElement.metadata.locked}
            onChange={(checked) => handleMetadataChange('locked', checked)}
          />
        </PropertySection>
      </div>
    </div>
  );
};

export default PropertiesPanel;
